import { createContext, ReactNode, useCallback, useContext, useMemo, useState, Dispatch, SetStateAction } from 'react';
import { useLocalStorage } from 'usehooks-ts';

const INVITE_PUBLIC_ID_STORAGE_KEY = 'invitePublicId';
const PRESIGNUP_TEXT_STORAGE_KEY = 'presignupText';

type PreSignUpText = { text: string; title: string };

const landingContext = createContext<{
  invitePublicId?: string;
  preSignUpText?: PreSignUpText;
  setInvitePublicId: Dispatch<SetStateAction<string>>;
  setPreSignUpText: (preSignUpText?: PreSignUpText) => void;
  surveyResponseId?: string;
  setSurveyResponseId: (id?: string) => void;
}>({
  invitePublicId: undefined,
  preSignUpText: undefined,
  setInvitePublicId: () => undefined,
  setPreSignUpText: () => undefined,
  setSurveyResponseId: () => undefined,
  surveyResponseId: undefined,
});

const LandingContext = ({ children }: { readonly children: ReactNode }) => {
  const [invitePublicId, setInvitePublicId] = useLocalStorage(INVITE_PUBLIC_ID_STORAGE_KEY, '');
  const [surveyResponseId, setSurveyResponseId] = useState<string | undefined>();
  const [preSignUpText, setPreSignUpText] = useLocalStorage<PreSignUpText | undefined>(
    PRESIGNUP_TEXT_STORAGE_KEY,
    undefined
  );

  const contextValue = useMemo(
    () => ({
      invitePublicId,
      preSignUpText,
      setInvitePublicId,
      setPreSignUpText,
      setSurveyResponseId,
      surveyResponseId,
    }),
    [invitePublicId, setInvitePublicId, surveyResponseId, setSurveyResponseId, preSignUpText, setPreSignUpText]
  );

  return <landingContext.Provider value={contextValue}>{children}</landingContext.Provider>;
};

const useLandingContext = () => useContext(landingContext);

const useGetAuthSessionContext = () => {
  const { invitePublicId } = useLandingContext();

  return useCallback(
    () => ({
      invitePublicId,
    }),
    [invitePublicId]
  );
};

export { LandingContext, useGetAuthSessionContext, useLandingContext };
